import axios, { AxiosInstance, CancelTokenSource, InternalAxiosRequestConfig } from 'axios';

import { FeatureFlagsManager } from '../../core/feature-flags-manager';
import { isDefined } from '../../utils/variables';
import { TokenService } from '../authentication/token.service';
import { AccessTokenService } from './services/v0/access-token-service';
import { AccountService } from './services/v0/account-service';
import { AuthenticationService } from './services/v0/authentication-service';
import { CompanyService } from './services/v0/company-service';
import { DynamicAssetService } from './services/v0/dynamic-asset-service';
import { GeodataService } from './services/v0/geodata-service';
import { OrthoTileService } from './services/v0/ortho-tile-service';
import { PointCloudService } from './services/v0/point-cloud-service';
import { ProjectService } from './services/v0/project-service';
import { ScanService } from './services/v0/scan-service';
import { SharedViewService } from './services/v0/shared-view-service';
import { StaticAssetService } from './services/v0/static-asset-service';
import { TerrainModelService } from './services/v0/terrain-model-service';
import { TimelineServiceV0 } from './services/v0/timeline-service';
import { UserServiceV0 } from './services/v0/user-service';
import { AssetService } from './services/v1/asset-service';
import { AuthService } from './services/v1/auth-service';
import { CompanyServiceV1 } from './services/v1/company-service';
import { CoordinateSystemService } from './services/v1/coordinate-system-service';
import { DongleServiceV1 } from './services/v1/dongle-service';
import { FileServiceV1 } from './services/v1/file-service';
import { FileVersionServiceV1 } from './services/v1/file-version-service';
import { FolderService as FolderServiceV1 } from './services/v1/folder-service';
import { GeoidService } from './services/v1/geoid-service';
import { ProjectService as ProjectServiceV1 } from './services/v1/project-service';
import { SharedFolderServiceV1 } from './services/v1/shared-folder';
import { SharedViewServiceV1 } from './services/v1/shared-view-service';
import { TiledModelServiceV1 } from './services/v1/tiled-model-service';
import { UploadServiceV1 } from './services/v1/upload-service';
import { UserServiceV1 } from './services/v1/user-service';

export class ApiResponseError extends Error {}

class SkyMapAxiosServiceFactory {
  static get instance() {
    return SkyMapAxiosServiceFactory._instance;
  }

  /**
   *  Because axiosInstance is not setup until setup, it is possible undefined.
   *  TODO: Remove dependency to
   */
  private static axiosInstance?: AxiosInstance;

  private static _instance: SkyMapAxiosServiceFactory = new SkyMapAxiosServiceFactory();

  /**
   *  Attaches the authentication bearer token to all request if available.
   *
   * @param tokenService - The AngularJs service that contains the token. TODO: Remove AngularJs dependency.
   */
  static requestInterceptor(tokenService: TokenService) {
    return (config: InternalAxiosRequestConfig) => {
      const token = tokenService.get();

      config.headers = config.headers ?? {};
      if (isDefined(token)) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (FeatureFlagsManager.instance.isEnabled('SK-4477-access-management')) {
        config.headers['Enable-Cerbos-Check'] = true;
      }

      return config;
    };
  }

  /**
   *  Intercepts any error when executing requests. Prints the error and throws a promise rejection.
   *
   * @param error
   */
  static requestErrorInterceptor(error: any) {
    console.log(JSON.stringify(error));
    return Promise.reject(error);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   *  Setup axios factory with the base url and token service.
   *  TODO: Remove AngularJs service dependency.
   *
   * @param url - The API base url.
   * @param tokenService - The AngularJS service that contains the token.
   */
  setup(url: string, tokenService: TokenService) {
    SkyMapAxiosServiceFactory.axiosInstance = axios.create({
      baseURL: url,
    });

    SkyMapAxiosServiceFactory.axiosInstance.interceptors.request.use(
      SkyMapAxiosServiceFactory.requestInterceptor(tokenService),
      void SkyMapAxiosServiceFactory.requestErrorInterceptor,
    );

    SkyMapAxiosServiceFactory.axiosInstance.interceptors.response.use(
      (value) => value,
      (error) => Promise.reject(error),
    );
  }

  createAuthenticationServiceV0() {
    return new AuthenticationService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAuthServiceV1() {
    return new AuthService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createPointCloudServiceV0() {
    return new PointCloudService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createDynamicAssetServiceV0() {
    return new DynamicAssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createStaticAssetServiceV0() {
    return new StaticAssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedViewServiceV0() {
    return new SharedViewService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createScanServiceV0() {
    return new ScanService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createOrthoTileServiceV0() {
    return new OrthoTileService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTerrainModelServiceV0() {
    return new TerrainModelService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAccountServiceV0() {
    return new AccountService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createGeodataServiceV0() {
    return new GeodataService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createProjectServiceV0(cancelTokenSource?: CancelTokenSource) {
    return new ProjectService(SkyMapAxiosServiceFactory.axiosInstance!, cancelTokenSource);
  }

  createCompanyServiceV0() {
    return new CompanyService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAccessTokenServiceV0() {
    return new AccessTokenService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUserServiceV0() {
    return new UserServiceV0(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createProjectServiceV1() {
    return new ProjectServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFileServiceV1() {
    return new FileServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createCompanyServiceV1() {
    return new CompanyServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFileVersionServiceV1() {
    return new FileVersionServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createFolderServiceV1() {
    return new FolderServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTimelineServiceV0() {
    return new TimelineServiceV0(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createTiledModelServiceV1() {
    return new TiledModelServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedFolderServiceV1() {
    return new SharedFolderServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createCoordinateSystemServiceV1() {
    return new CoordinateSystemService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createGeoidServiceV1() {
    return new GeoidService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createAssetServiceV1() {
    return new AssetService(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUserServiceV1() {
    return new UserServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createUploadServiceV1() {
    return new UploadServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createSharedViewServiceV1() {
    return new SharedViewServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }

  createDongleServiceV1() {
    return new DongleServiceV1(SkyMapAxiosServiceFactory.axiosInstance!);
  }
}

export { SkyMapAxiosServiceFactory };
